import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { UnloggedScreenLayout } from '@/components/UnloggedScreenLayout/UnloggedScreenLayout'
import { AppRoutes } from '@/constants/appRoutes'
import {
  challengeSchema,
  securityErrorKey,
  sessionSchema,
  useSensitiveRequest,
} from '@/features/security/securityClient'
import type { MfaChallenge } from '@/features/security/securityClient'
import { useSSO } from '@/hooks/useSSO'
import { MfaChallengeForm } from '@/pages/Login/MfaChallengeForm'

export const AuthSuccess = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { successSSO, continueSsoInvite } = useSSO()
  const { t } = useTranslation()
  const request = useSensitiveRequest()
  const started = useRef(false)
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (started.current) return
    started.current = true
    if (searchParams.get('mfa') === 'required') {
      window.history.replaceState(
        window.history.state,
        '',
        AppRoutes.authSuccess,
      )
      void request('/api/v1/auth/mfa/handoff', challengeSchema)
        .then(setChallenge)
        .catch((e) => setError(securityErrorKey(e)))
      return
    }
    const ssoNonce = searchParams.get('ssoNonce')

    if (ssoNonce) {
      const email = searchParams.get('email') ?? ''
      continueSsoInvite({
        ssoNonce,
        email,
        firstName: searchParams.get('firstName') ?? '',
        lastName: searchParams.get('lastName') ?? '',
      })
      return
    }

    const session = sessionSchema.safeParse({
      token: searchParams.get('token'),
      userStatus: searchParams.get('status'),
    })
    const email = searchParams.get('email')

    if (!session.success || !email) {
      navigate(AppRoutes.login)
      return
    }

    window.history.replaceState(window.history.state, '', AppRoutes.authSuccess)
    successSSO(session.data.token, session.data.userStatus, email)
  }, [searchParams, navigate, request, successSSO, continueSsoInvite])

  return (
    <UnloggedScreenLayout>
      {challenge ? (
        <MfaChallengeForm
          challenge={challenge}
          onComplete={(session) =>
            successSSO(session.token, session.userStatus, '')
          }
          onCancel={() => {
            setChallenge(null)
            navigate(AppRoutes.login, { replace: true })
          }}
        />
      ) : (
        <div className="flex max-w-md flex-col gap-4">
          {error ? (
            <>
              <p role="alert">{t(error)}</p>
              <Button
                type="button"
                onClick={() => navigate(AppRoutes.login, { replace: true })}
              >
                {t('security.backToSignIn')}
              </Button>
            </>
          ) : (
            <p role="status">{t('security.loading')}</p>
          )}
        </div>
      )}
    </UnloggedScreenLayout>
  )
}

export default AuthSuccess
